import React, { useState, useEffect, useMemo, useCallback } from "react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const INK = "#0A0A0F";
const PAPER = "#F4F2EC";
const WHITE = "#FFFFFF";
const ACCENT = "#0033FF";
const ACCENT_SOFT = "rgba(0,51,255,0.08)";
const MUTED = "rgba(10,10,15,0.58)";
const HAIR = "rgba(10,10,15,0.14)";
const GOOD = "#0F7B57";
const WARN = "#DA9A00";
const CRIT = "#95192E";
const MONO = "'JetBrains Mono', monospace";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const mLabel = (m) => `${MONTH_SHORT[parseInt(m.slice(5), 10) - 1]} ${m.slice(2, 4)}`;
const fmt = (n) => Math.round(n).toLocaleString();
const money = (n) => `$${Math.round(n).toLocaleString()}`;
const isHolidayMonth = (m) => m.slice(5) === "11" || m.slice(5) === "12";

const STATUS = {
  ok: { label: "Covered", color: GOOD },
  print: { label: "Print", color: WARN },
  urgent: { label: "Restock", color: CRIT },
  mto: { label: "Made to order", color: MUTED },
  none: { label: "No sales", color: MUTED },
};

const chip = (on) => ({
  fontFamily: MONO, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
  textTransform: "uppercase", padding: "6px 11px", borderRadius: 0, cursor: "pointer",
  border: `1.5px solid ${on ? ACCENT : INK}`, background: on ? ACCENT : WHITE,
  color: on ? WHITE : INK,
});

const ctlBox = { padding: "12px 16px", borderRight: `2px solid ${INK}`, display: "flex", flexDirection: "column", gap: 7, justifyContent: "center" };
const labelSty = { fontFamily: MONO, fontSize: 10, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED };
const inputSty = { fontFamily: MONO, fontSize: 13, padding: "7px 9px", border: `1.5px solid ${INK}`, borderRadius: 0, background: WHITE, color: INK, width: "100%" };
const thSty = { fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED, textAlign: "right", padding: "10px 11px", borderBottom: `2px solid ${INK}`, cursor: "pointer", whiteSpace: "nowrap", userSelect: "none", background: PAPER };
const tdSty = { padding: "9px 11px", borderBottom: `1px solid ${HAIR}`, textAlign: "right", fontFamily: MONO, fontSize: 12.5, fontVariantNumeric: "tabular-nums", verticalAlign: "middle" };

function Spark({ series, active }) {
  const w = 104, h = 26, n = series.length;
  const max = Math.max(1, ...series);
  const bw = w / n;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }} aria-hidden="true">
      {series.map((v, i) => {
        const bh = (v / max) * (h - 3);
        return <rect key={i} x={i * bw} y={h - bh} width={Math.max(1, bw - 1)}
          height={Math.max(v > 0 ? 1 : 0, bh)} fill={active.has(i) ? ACCENT : HAIR} />;
      })}
    </svg>
  );
}

function MonthChart({ months, totals, active, partialIdx, onPick }) {
  const W = 1000, H = 210, padL = 44, padB = 24, padT = 8;
  const max = Math.max(1, ...totals);
  const iw = W - padL - 8, ih = H - padB - padT;
  const bw = iw / months.length;
  const gap = Math.min(4, bw * 0.22);
  const y = (v) => padT + ih - (v / max) * ih;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ display: "block", width: "100%", height: 210 }}
      role="img" aria-label="Pieces sold per month">
      {[0, 1, 2, 3].map((g) => {
        const v = (max * g) / 3;
        return (
          <g key={g}>
            <line x1={padL} y1={y(v)} x2={W - 8} y2={y(v)} stroke={HAIR} strokeWidth="1" />
            <text x={padL - 7} y={y(v) + 3.5} textAnchor="end" fontFamily={MONO} fontSize="9.5" fill={MUTED}>{Math.round(v)}</text>
          </g>
        );
      })}
      {months.map((m, i) => {
        const x = padL + i * bw + gap / 2;
        const bwid = Math.max(2, bw - gap);
        const hgt = Math.max(totals[i] > 0 ? 2 : 0, padT + ih - y(totals[i]));
        return (
          <g key={m} onClick={() => onPick(i)} style={{ cursor: "pointer" }}>
            <title>{`${mLabel(m)} — ${fmt(totals[i])} pieces`}</title>
            <rect x={x} y={padT} width={bwid} height={ih} fill="transparent" />
            <rect x={x} y={padT + ih - hgt} width={bwid} height={hgt} rx="3"
              fill={active.has(i) ? ACCENT : HAIR} opacity={i === partialIdx ? 0.45 : 1} />
            {i % (months.length > 14 ? 2 : 1) === 0 && (
              <text x={x + bwid / 2} y={H - 8} textAnchor="middle" fontFamily={MONO} fontSize="9.5" fill={MUTED}>{mLabel(m)}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export default function EtsySalesTab({ store }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState({ a: 0, b: 0 });
  // Only ever read through the functional setter (click-a-bar range picking).
  const [, setAnchor] = useState(null);
  const [exHoliday, setExHoliday] = useState(false);
  const [hideCustom, setHideCustom] = useState(false);
  const [onlyStock, setOnlyStock] = useState(false);
  const [query, setQuery] = useState("");
  const [cover, setCover] = useState(2);
  const [sortKey, setSortKey] = useState("pieces");
  const [sortDir, setSortDir] = useState(-1);
  const [openRow, setOpenRow] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/etsy/sales-rollup?store=${store}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d.error) { setError(d.error); setData(null); }
        else {
          setData(d);
          const n = (d.months || []).length;
          // Default to every closed month; the live month is excluded so a
          // part-finished month can't drag the averages down.
          const last = d.liveMonth && d.months[n - 1] === d.liveMonth ? n - 2 : n - 1;
          setRange({ a: 0, b: Math.max(0, last) });
        }
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(String(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [store]);

  const months = useMemo(() => (data && data.months) || [], [data]);
  const products = useMemo(() => (data && data.products) || [], [data]);
  const partialIdx = useMemo(
    () => (data && data.liveMonth ? months.indexOf(data.liveMonth) : -1),
    [data, months]
  );
  const lastClosed = partialIdx >= 0 ? partialIdx - 1 : months.length - 1;

  // Active months = selected range minus any switched off. Nov/Dec run several
  // times a normal month, so averaging them in overstates routine restock needs.
  const active = useMemo(() => {
    const s = new Set();
    for (let i = range.a; i <= range.b; i++) {
      if (exHoliday && isHolidayMonth(months[i] || "")) continue;
      s.add(i);
    }
    return s;
  }, [range, exHoliday, months]);

  const activeList = useMemo(() => [...active].sort((x, y) => x - y), [active]);
  const span = Math.max(1, activeList.length);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = products.map((p) => {
      let pieces = 0, units = 0, rev = 0, peakI = -1, peakV = -1;
      for (const i of activeList) {
        pieces += p.p[i] || 0; units += p.o[i] || 0; rev += p.r[i] || 0;
        if ((p.p[i] || 0) > peakV) { peakV = p.p[i] || 0; peakI = i; }
      }
      const avg = pieces / span;
      const stock = p.stock === null || p.stock === undefined ? null : p.stock;
      let need = null, status = "none";
      if (p.custom) status = "mto";
      else {
        const target = avg * cover;
        need = Math.max(0, Math.ceil(target - (stock || 0)));
        if (avg < 0.05) status = "none";
        else if (avg >= 5 && (stock || 0) <= 0) status = "urgent";
        else if (need > 0) status = "print";
        else status = "ok";
      }
      return { p, pieces, units, rev, avg, peakI, peakV, stock, need, status };
    });
    if (hideCustom) out = out.filter((r) => !r.p.custom);
    if (onlyStock) out = out.filter((r) => r.stock !== null);
    if (q) out = out.filter((r) =>
      r.p.id.toLowerCase().includes(q) || (r.p.t || "").toLowerCase().includes(q) ||
      (r.p.var || "").toLowerCase().includes(q));
    const keyFn = {
      id: (r) => r.p.id.toLowerCase(), t: (r) => (r.p.t || "").toLowerCase(),
      pieces: (r) => r.pieces, avg: (r) => r.avg, units: (r) => r.units,
      rev: (r) => r.rev, peak: (r) => r.peakV, stock: (r) => (r.stock === null ? -1 : r.stock),
      need: (r) => (r.need === null ? -1 : r.need),
      status: (r) => ["urgent", "print", "ok", "mto", "none"].indexOf(r.status),
    }[sortKey] || ((r) => r.pieces);
    return out.sort((x, y) => {
      const kx = keyFn(x), ky = keyFn(y);
      if (kx < ky) return -sortDir;
      if (kx > ky) return sortDir;
      return 0;
    });
  }, [products, activeList, span, cover, hideCustom, onlyStock, query, sortKey, sortDir]);

  const totalsByMonth = useMemo(
    () => months.map((_, i) => products.reduce((s, p) => s + (p.p[i] || 0), 0)),
    [months, products]
  );

  const tot = useMemo(() => rows.reduce((s, r) => {
    s.pieces += r.pieces; s.units += r.units; s.rev += r.rev;
    if (r.pieces > 0) s.active += 1;
    if (!r.p.custom) s.need += r.need || 0;
    return s;
  }, { pieces: 0, units: 0, rev: 0, active: 0, need: 0 }), [rows]);

  const pickBar = useCallback((i) => {
    setAnchor((prev) => {
      if (prev === null) { setRange({ a: i, b: i }); return i; }
      setRange({ a: Math.min(prev, i), b: Math.max(prev, i) });
      return null;
    });
  }, []);

  const preset = useCallback((kind) => {
    setAnchor(null);
    if (kind === "all") setRange({ a: 0, b: lastClosed });
    else if (kind === "holiday") {
      const i = months.indexOf("2025-11"), j = months.indexOf("2025-12");
      if (i >= 0) setRange({ a: i, b: j < 0 ? i : j });
    } else setRange({ a: Math.max(0, lastClosed - (kind - 1)), b: lastClosed });
  }, [lastClosed, months]);

  const exportCsv = () => {
    const head = ["sku_or_listing", "product", "variation", "pack_size", "pieces", "avg_per_month",
      "units", "item_revenue", "peak_month", "current_stock", "suggested_print", "status",
      "made_to_order", "window_from", "window_to", "months_averaged", "holidays_excluded"];
    const esc = (v) => {
      const s = String(v === null || v === undefined ? "" : v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = rows.map((r) => [r.p.id, r.p.t, r.p.var, r.p.pk, r.pieces, r.avg.toFixed(2),
      r.units, r.rev.toFixed(2), r.peakI >= 0 ? months[r.peakI] : "", r.stock, r.need,
      STATUS[r.status].label, r.p.custom ? "yes" : "no", months[range.a], months[range.b],
      activeList.length, exHoliday ? "yes" : "no"].map(esc).join(","));
    const blob = new Blob([[head.join(","), ...body].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${store}_sales_${months[range.a]}_to_${months[range.b]}${exHoliday ? "_no_holiday" : ""}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: MUTED, fontFamily: MONO }}>Loading sales history…</div>;
  if (error) return <div style={{ padding: 24, border: `2px solid ${CRIT}`, color: CRIT, fontFamily: MONO, fontSize: 13 }}>{error}</div>;
  if (!data || !months.length) {
    return (
      <div style={{ padding: 40, textAlign: "center", border: `2px solid ${INK}`, background: WHITE }}>
        <div style={{ fontFamily: MONO, fontSize: 14, marginBottom: 8 }}>
          {data && data.connected === false ? "This shop isn't connected to Etsy." : "No sales history stored yet."}
        </div>
        <div style={{ fontSize: 13, color: MUTED }}>
          {data && data.connected === false
            ? "Connect it on the Etsy page and the rollup will backfill its history automatically."
            : `The monthly rollup runs in the background and backfills the shop's full history on its first pass.${
                data && data.pendingMonths ? ` ${data.pendingMonths} months queued.` : ""}`}
        </div>
      </div>
    );
  }

  const cols = [
    ["", "", 40], ["id", "SKU", 0], ["t", "Product", 0], ["spark", "Trend", 0],
    ["pieces", "Pieces", 0], ["avg", "Avg/mo", 0], ["units", "Units", 0],
    ["rev", "Item rev", 0], ["peak", "Peak mo", 0], ["stock", "Stock", 0],
    ["need", "Print", 0], ["status", "Status", 0],
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      <div style={{ border: `2px solid ${INK}`, background: WHITE, display: "flex", flexWrap: "wrap" }}>
        <div style={ctlBox}>
          <span style={labelSty}>Window</span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["all", "All"], [12, "12 mo"], [6, "6 mo"], [3, "3 mo"], ["holiday", "Holiday"]].map(([k, l]) => (
              <button key={l} onClick={() => preset(k)} style={chip(false)}>{l}</button>
            ))}
          </div>
        </div>
        <div style={ctlBox}>
          <span style={labelSty}>Custom range</span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <select value={range.a} onChange={(e) => { const v = +e.target.value; setAnchor(null); setRange((r) => ({ a: v, b: Math.max(v, r.b) })); }} style={inputSty}>
              {months.map((m, i) => <option key={m} value={i}>{mLabel(m)}{i === partialIdx ? " (partial)" : ""}</option>)}
            </select>
            <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>to</span>
            <select value={range.b} onChange={(e) => { const v = +e.target.value; setAnchor(null); setRange((r) => ({ a: Math.min(v, r.a), b: v })); }} style={inputSty}>
              {months.map((m, i) => <option key={m} value={i}>{mLabel(m)}{i === partialIdx ? " (partial)" : ""}</option>)}
            </select>
          </div>
        </div>
        <div style={{ ...ctlBox, flex: 1, minWidth: 180 }}>
          <span style={labelSty}>Search</span>
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="SKU or product name…" style={inputSty} />
        </div>
        <div style={ctlBox}>
          <span style={labelSty}>Cover</span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="number" min="0.5" max="12" step="0.5" value={cover}
              onChange={(e) => { const v = parseFloat(e.target.value); if (v > 0) setCover(v); }}
              style={{ ...inputSty, width: 76 }} />
            <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>months</span>
          </div>
        </div>
        <div style={{ ...ctlBox, borderRight: "none" }}>
          <span style={labelSty}>Show</span>
          {[["Exclude Nov + Dec", exHoliday, setExHoliday],
            ["Hide made-to-order", hideCustom, setHideCustom],
            ["Only tracked stock", onlyStock, setOnlyStock]].map(([l, v, set]) => (
            <label key={l} style={{ display: "flex", gap: 8, alignItems: "center", fontFamily: MONO, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={v} onChange={(e) => set(e.target.checked)} style={{ accentColor: ACCENT, width: 15, height: 15, margin: 0 }} />
              {l}
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))" }}>
        {[["Pieces sold", fmt(tot.pieces), `${activeList.length} months${exHoliday ? " · no Nov/Dec" : ""}`],
          ["Per month", (tot.pieces / span).toFixed(tot.pieces / span < 10 ? 1 : 0), "average across window"],
          ["Units ordered", fmt(tot.units), `${(tot.units / span).toFixed(0)}/mo`],
          ["Item revenue", money(tot.rev), `${money(tot.rev / span)}/mo`],
          ["Products selling", fmt(tot.active), `of ${rows.length} shown`]].map(([l, v, s]) => (
          <div key={l} style={{ border: `2px solid ${INK}`, background: WHITE, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={labelSty}>{l}</span>
            <b style={{ fontFamily: MONO, fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{v}</b>
            <small style={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>{s}</small>
          </div>
        ))}
        <div style={{ border: `2px solid ${ACCENT}`, background: ACCENT, color: WHITE, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ ...labelSty, color: "rgba(255,255,255,0.82)" }}>Suggested print</span>
          <b style={{ fontFamily: MONO, fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{fmt(tot.need)}</b>
          <small style={{ fontFamily: MONO, fontSize: 11, color: "rgba(255,255,255,0.82)" }}>pieces · {cover} mo cover</small>
        </div>
      </div>

      <div style={{ border: `2px solid ${INK}`, background: WHITE }}>
        <div style={{ padding: "12px 18px", borderBottom: `2px solid ${INK}`, display: "flex", gap: 14, alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.02em" }}>Pieces per month</span>
          <span style={{ fontSize: 12.5, color: MUTED }}>Click a bar to start a range, click a second to close it.</span>
        </div>
        <div style={{ padding: 18 }}>
          <MonthChart months={months} totals={totalsByMonth} active={active} partialIdx={partialIdx} onPick={pickBar} />
        </div>
      </div>

      <div style={{ border: `2px solid ${INK}`, background: WHITE }}>
        <div style={{ padding: "12px 18px", borderBottom: `2px solid ${INK}`, display: "flex", gap: 14, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.02em" }}>By product</span>
          <button onClick={exportCsv} style={chip(false)}>Export view · CSV</button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 980 }}>
            <thead>
              <tr>
                {cols.map(([k, l, w], i) => (
                  <th key={l + i} onClick={() => { if (!k) return; if (sortKey === k) setSortDir(-sortDir); else { setSortKey(k); setSortDir(k === "id" || k === "t" ? 1 : -1); } }}
                    style={{ ...thSty, width: w || undefined, textAlign: i <= 2 ? "left" : "right", cursor: k ? "pointer" : "default", color: sortKey === k ? INK : MUTED }}>
                    {l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const rowKey = r.p.k + r.p.id + (r.p.var || "");
                const isOpen = openRow === rowKey;
                const st = STATUS[r.status];
                return (
                  <React.Fragment key={rowKey}>
                    <tr onClick={() => setOpenRow(isOpen ? null : rowKey)}
                      style={{ cursor: "pointer", background: isOpen ? ACCENT_SOFT : "transparent" }}>
                      <td style={{ ...tdSty, textAlign: "left", color: MUTED, fontSize: 11 }}>{idx + 1}</td>
                      <td style={{ ...tdSty, textAlign: "left", fontWeight: 700, whiteSpace: "nowrap" }}>
                        {r.p.k === "sku" ? r.p.id : `#${r.p.id}`}
                        {r.p.custom && <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", padding: "2px 6px", border: `1.5px solid ${WARN}`, color: WARN }}>MTO</span>}
                        {r.p.k === "listing" && <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", padding: "2px 6px", border: `1.5px solid ${MUTED}`, color: MUTED }}>NO SKU</span>}
                      </td>
                      <td style={{ ...tdSty, textAlign: "left", fontFamily: "inherit", fontSize: 13, maxWidth: 230, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={r.p.t}>{r.p.t}{r.p.var ? ` · ${r.p.var}` : ""}</td>
                      <td style={tdSty}><Spark series={r.p.p} active={active} /></td>
                      <td style={{ ...tdSty, fontWeight: 700, fontSize: 13.5 }}>{fmt(r.pieces)}</td>
                      <td style={tdSty}>{r.avg >= 10 ? Math.round(r.avg) : r.avg.toFixed(1)}</td>
                      <td style={{ ...tdSty, color: MUTED }}>{fmt(r.units)}</td>
                      <td style={{ ...tdSty, color: MUTED }}>{money(r.rev)}</td>
                      <td style={{ ...tdSty, color: MUTED }}>{r.peakI >= 0 && r.peakV > 0 ? mLabel(months[r.peakI]) : "—"}</td>
                      <td style={tdSty}>{r.stock === null ? <span style={{ color: MUTED }}>—</span> : fmt(r.stock)}</td>
                      <td style={{ ...tdSty, fontWeight: 700, fontSize: 13.5 }}>{r.need === null ? <span style={{ color: MUTED }}>—</span> : fmt(r.need)}</td>
                      <td style={{ ...tdSty, whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: st.color, fontWeight: 700, fontSize: 11, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                          {r.status !== "mto" && r.status !== "none" && <span style={{ width: 9, height: 9, background: st.color, display: "inline-block" }} />}
                          {st.label}
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={12} style={{ background: PAPER, padding: "16px 18px", borderBottom: `2px solid ${INK}` }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(74px,1fr))", gap: 3, marginBottom: 14 }}>
                            {months.map((m, i) => (
                              <div key={m} style={{ border: `1px solid ${active.has(i) ? ACCENT : HAIR}`, background: active.has(i) ? ACCENT_SOFT : WHITE, padding: "6px 4px", textAlign: "center" }}>
                                <em style={{ display: "block", fontFamily: MONO, fontSize: 9, fontStyle: "normal", color: MUTED }}>{mLabel(m)}</em>
                                <b style={{ display: "block", fontFamily: MONO, fontSize: 14, fontVariantNumeric: "tabular-nums" }}>{r.p.p[i] || 0}</b>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
                            {[["Listing", r.p.lid || "—"], ["Pack size", r.p.pk], ["Method", r.p.mfg || "—"],
                              ["Lifetime pieces", fmt(r.p.p.reduce((s, v) => s + v, 0))],
                              ["Variations", r.p.nvar || "—"],
                              ["Refunded", r.p.refunded ? money(r.p.refunded) : "—"]].map(([k, v]) => (
                              <div key={k} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <span style={labelSty}>{k}</span>
                                <b style={{ fontFamily: MONO, fontSize: 13 }}>{v}</b>
                              </div>
                            ))}
                            {r.p.tvar && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <span style={labelSty}>Top variations</span>
                                <b style={{ fontFamily: MONO, fontSize: 13 }}>{r.p.tvar}</b>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {!rows.length && <div style={{ padding: 40, textAlign: "center", color: MUTED, fontFamily: MONO, fontSize: 13 }}>
          {activeList.length === 0 ? "That range is only Nov/Dec, and those are switched off." : "Nothing matches those filters."}
        </div>}
      </div>

      <div style={{ fontSize: 12.5, color: MUTED, display: "flex", flexDirection: "column", gap: 8, maxWidth: "78ch" }}>
        <div><b>Print</b> = (avg pieces/month × cover months) − current stock. It follows whatever window you pick, so a summer window plans for summer and a Nov–Dec window plans for the rush.</div>
        <div><b>Pieces</b> are pack-expanded, so one order of a 3-pack counts as 3. A refunded order that still shipped keeps counting, since the piece was printed either way; only orders that never shipped are dropped.</div>
        <div>History is stored per month and refreshed automatically after each month closes{data.lastUpdated ? `. Last updated ${new Date(data.lastUpdated).toLocaleDateString()}` : ""}.</div>
      </div>
    </div>
  );
}
